<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  subscriptionStatus: {
    type: String,
    default: 'inactive',
    validator: value => ['inactive', 'expired', 'canceled', 'cancelled', 'past_due', 'trialing', 'active', 'incomplete', 'paused', 'revoked', 'incomplete_expired', 'loading'].includes(value),
  },
  daysLeft: {
    type: Number,
    default: 0,
  },
});

const { t } = useI18n();
const { accountId } = useAccount();

const goToBillingSettings = () => {
  window.location.href = `/app/accounts/${accountId.value}/settings/billing`;
};

const paywallConfig = computed(() => {
  switch (props.subscriptionStatus) {
    case 'expired':
    case 'revoked':
    case 'incomplete_expired':
      return {
        icon: '🔒',
        title: t('REPLY_PAYWALL.EXPIRED.TITLE'),
        description: t('REPLY_PAYWALL.EXPIRED.DESCRIPTION'),
        ctaText: t('REPLY_PAYWALL.EXPIRED.CTA'),
        urgency: false,
      };
    case 'canceled':
    case 'cancelled':
      return {
        icon: '⚠️',
        title: t('REPLY_PAYWALL.CANCELED.TITLE'),
        description: t('REPLY_PAYWALL.CANCELED.DESCRIPTION'),
        ctaText: t('REPLY_PAYWALL.CANCELED.CTA'),
        urgency: true,
      };
    case 'trialing':
      return {
        icon: '⚡',
        title: t('REPLY_PAYWALL.TRIAL.TITLE', { days: props.daysLeft }),
        description: t('REPLY_PAYWALL.TRIAL.DESCRIPTION'),
        ctaText: t('REPLY_PAYWALL.TRIAL.CTA'),
        urgency: props.daysLeft <= 3,
      };
    case 'past_due':
      return {
        icon: '💳',
        title: t('REPLY_PAYWALL.PAST_DUE.TITLE'),
        description: t('REPLY_PAYWALL.PAST_DUE.DESCRIPTION'),
        ctaText: t('REPLY_PAYWALL.PAST_DUE.CTA'),
        urgency: true,
      };
    case 'incomplete':
      return {
        icon: '⏳',
        title: t('REPLY_PAYWALL.INCOMPLETE.TITLE'),
        description: t('REPLY_PAYWALL.INCOMPLETE.DESCRIPTION'),
        ctaText: t('REPLY_PAYWALL.INCOMPLETE.CTA'),
        urgency: false,
      };
    case 'paused':
      return {
        icon: '⏸️',
        title: t('REPLY_PAYWALL.PAUSED.TITLE'),
        description: t('REPLY_PAYWALL.PAUSED.DESCRIPTION'),
        ctaText: t('REPLY_PAYWALL.PAUSED.CTA'),
        urgency: false,
      };
    case 'loading':
      return null; // Don't show overlay while loading
    case 'inactive':
    default:
      return {
        icon: '🔒',
        title: t('REPLY_PAYWALL.INACTIVE.TITLE'),
        description: t('REPLY_PAYWALL.INACTIVE.DESCRIPTION'),
        ctaText: t('REPLY_PAYWALL.INACTIVE.CTA'),
        urgency: false,
      };
  }
});
</script>

<template>
  <div v-if="paywallConfig" class="reply-paywall-overlay">
    <div class="paywall-card" :class="{ urgent: paywallConfig.urgency }">
      <div class="paywall-icon">{{ paywallConfig.icon }}</div>

      <div class="paywall-content">
        <h3 class="paywall-title">{{ paywallConfig.title }}</h3>
        <p class="paywall-description">{{ paywallConfig.description }}</p>

        <div class="paywall-features">
          <div class="feature-item">
            <span class="feature-check">✓</span>
            <span>Unlimited conversations</span>
          </div>
          <div class="feature-item">
            <span class="feature-check">✓</span>
            <span>Sage AI Assistant</span>
          </div>
          <div class="feature-item">
            <span class="feature-check">✓</span>
            <span>All channels included</span>
          </div>
        </div>
      </div>

      <Button variant="solid" color="blue" size="lg" class="paywall-cta" @click="goToBillingSettings">
        {{ paywallConfig.ctaText }}
      </Button>

      <p class="paywall-note">
        {{ $t('REPLY_PAYWALL.NO_CC') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.reply-paywall-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--n-alpha-3), 0.4) 0%,
    rgba(var(--n-alpha-3), 0.98) 60%,
    rgba(var(--n-alpha-3), 1) 100%
  );
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
}

.paywall-card {
  background: var(--n-solid-1);
  border: 1px solid var(--n-strong);
  border-radius: 16px;
  padding: 32px 28px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  animation: paywallSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes paywallSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.paywall-card.urgent {
  border-color: var(--n-ruby-7);
  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(var(--n-ruby-6), 0.3);
}

.paywall-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, var(--n-violet-9), var(--n-brand));
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  box-shadow: 0 8px 24px rgba(var(--n-brand), 0.3);
}

.paywall-card.urgent .paywall-icon {
  background: linear-gradient(135deg, var(--n-ruby-9), var(--n-amber-9));
  box-shadow: 0 8px 24px rgba(var(--n-ruby-6), 0.3);
}

.paywall-title {
  font-size: 22px;
  font-weight: 600;
  color: var(--n-slate-12);
  margin-bottom: 10px;
  line-height: 1.3;
}

.paywall-description {
  font-size: 15px;
  color: var(--n-slate-11);
  line-height: 1.6;
  margin-bottom: 24px;
}

.paywall-features {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 24px;
  padding: 20px;
  background: rgba(var(--n-alpha-2), 0.5);
  border-radius: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--n-slate-11);
}

.feature-check {
  color: var(--n-brand);
  font-weight: 700;
  font-size: 16px;
}

.paywall-cta {
  width: 100%;
  margin-bottom: 14px;
  font-weight: 600;
}

.paywall-note {
  font-size: 13px;
  color: var(--n-slate-10);
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.paywall-note::before {
  content: '🔒';
  font-size: 12px;
}
</style>
